import { SCHEDULE_SECTIONS, theme } from '../constants';
import { SearchIcon } from './Icons';
import SyncCell from './SyncCell';

const EmptyState = ({ title, subtitle }) => (
  <div style={{ padding: '52px 20px', textAlign: 'center' }}>
    <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.textMuted }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12.5, color: theme.textFaint, marginTop: 5 }}>{subtitle}</div>}
  </div>
);

const Skeletons = () => (
  <div style={{ padding: '14px 2px', display: 'flex', flexDirection: 'column', gap: 10 }}>
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="dsSkeleton" style={{ height: 56, opacity: 1 - row * 0.15 }} />
    ))}
  </div>
);

const headCell = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textFaint,
  padding: '0 12px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

/**
 * DeviceID / Name / Default Schedule Sync / Temp Schedule Sync for every
 * EnergiSync device on the selected property.
 */
function DeviceScheduleTable({
  devices,
  total,
  loading,
  hasHouse,
  search,
  onSearchChange,
  onEdit,
  savingKey,
}) {
  const renderBody = () => {
    if (!hasHouse) {
      return <EmptyState title="Select a property to begin" subtitle="Its EnergiSync devices will be listed here." />;
    }
    if (loading) return <Skeletons />;
    if (!total) {
      return <EmptyState title="No EnergiSync devices on this property" subtitle="Choose a different property." />;
    }
    if (!devices.length) {
      return <EmptyState title="No devices match this search" subtitle="Try a different device ID, name or room." />;
    }

    return (
      <div className="dsScroll" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 860 }}>
          <thead>
            <tr>
              <th style={{ ...headCell, width: 170 }}>DeviceID</th>
              <th style={{ ...headCell, width: 220 }}>Name</th>
              {SCHEDULE_SECTIONS.map((section) => (
                <th key={section.key} style={headCell}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: section.accent }} />
                    {section.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.deviceId} className="dsRow">
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: theme.text, fontFamily: 'monospace' }}>
                    {device.deviceId}
                  </div>
                  <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 3 }}>{device.roomName}</div>
                </td>
                <td style={{ padding: '9px 12px', verticalAlign: 'top' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: device.hasName ? theme.text : theme.textFaint }}>
                    {device.hasName ? device.name : 'Unnamed device'}
                  </div>
                </td>
                {SCHEDULE_SECTIONS.map((section) => (
                  <td key={section.key} style={{ padding: '9px 12px', verticalAlign: 'top', minWidth: 250 }}>
                    <SyncCell
                      section={section}
                      values={device.sections[section.key].current}
                      pending={device.sections[section.key].pending}
                      saving={savingKey === `${device.deviceId}:${section.key}`}
                      onEdit={() => onEdit(device, section)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: '16px 18px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text, lineHeight: '17px' }}>
            EnergiSync Devices
          </div>
          <div style={{ fontSize: 11.5, color: theme.textFaint, lineHeight: '16px' }}>
            {hasHouse ? `${devices.length} of ${total} shown` : 'Waiting for a property'}
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <span
            style={{
              position: 'absolute',
              left: 11,
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.textFaint,
              pointerEvents: 'none',
              display: 'flex',
            }}
          >
            <SearchIcon size={14} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search ID, name or room…"
            disabled={!hasHouse}
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              padding: '7px 12px 7px 32px',
              fontSize: 13,
              fontFamily: theme.font,
              outline: 'none',
              width: 250,
              color: '#374151',
              background: hasHouse ? theme.surface : theme.surfaceMuted,
            }}
          />
        </div>
      </div>

      {renderBody()}
    </div>
  );
}

export default DeviceScheduleTable;
